/**
 * A PV installation in one step (decision 0017): on the board, a dedicated circuit for the
 * inverter under its own RCD (AC side); on a new single-line sheet, the DC side — the strings,
 * a DC isolator per string, the DC surge protector, the inverter's DC inputs and the battery.
 * The DC-side symbols are of the `generation` category: they are not AC points, so the
 * circuit rules leave them alone and the PV rules (@elec/rules pv.ts) check them.
 */
import {
  applyTransaction,
  newId,
  type Element,
  type Id,
  type Op,
  type Project,
  type PropValue,
  type WireEnd,
} from '@elec/domain';
import { analyse } from '@elec/analysis';
import { appendGroupOps, type CircuitSpec } from './singleLine.ts';

export interface PvSpec {
  /** Single-line sheet of the board the inverter is connected to. */
  boardSheetId: Id;
  strings: Array<{ modules: number; moduleWp: number; vocV?: number }>;
  /** AC power of the inverter (kW). */
  inverterKw: number;
  phases: 1 | 3;
  /** Battery capacity (kWh); 0 or undefined for none. */
  batteryKwh?: number;
  /** RCD of the inverter circuit: type B unless the inverter's manual allows type A. */
  rcd: { sensitivityMa: number; rcdType: 'A' | 'B' };
  dcSpd: boolean;
  /** Name of the DC sheet. */
  sheetName: string;
  /** Label of the AC circuit. */
  circuitLabel: string;
}

const RATINGS = [16, 20, 25, 32, 40, 50, 63];
const SECTION: Record<number, number> = { 16: 2.5, 20: 2.5, 25: 4, 32: 6, 40: 10, 50: 16, 63: 16 };

/** Breaker for the inverter's output current with a 25 % margin, and its cable section. */
export function inverterProtection(
  kw: number,
  phases: 1 | 3,
): { ratingA: number; sectionMm2: number; currentA: number } {
  const currentA = (kw * 1000) / (phases === 3 ? Math.sqrt(3) * 400 : 230);
  const ratingA = RATINGS.find((r) => r >= currentA * 1.25) ?? 63;
  return { ratingA, sectionMm2: SECTION[ratingA]!, currentA: Math.round(currentA * 10) / 10 };
}

export function pvInstallationOps(
  project: Project,
  spec: PvSpec,
): { ops: Op[]; dcSheetId: Id; inverterTag: string } {
  const prot = inverterProtection(spec.inverterKw, spec.phases);
  const poles = spec.phases === 3 ? 4 : 2;
  const circuit: CircuitSpec = {
    label: spec.circuitLabel,
    device: { symbolId: 'iec.breaker', props: { ratingA: prot.ratingA, curve: 'C', poles } },
    points: [{ symbolId: 'iec.inverter', props: { powerKw: spec.inverterKw } }],
    cable: { type: 'XVB', cores: spec.phases === 3 ? 5 : 3, sectionMm2: prot.sectionMm2 },
  };
  const rcd: Record<string, PropValue> = {
    ratingA: prot.ratingA > 40 ? 63 : 40,
    sensitivityMa: spec.rcd.sensitivityMa,
    rcdType: spec.rcd.rcdType,
    poles,
  };
  const ops: Op[] = appendGroupOps(project, spec.boardSheetId, rcd, [circuit], analyse(project));
  let p = applyTransaction(project, { label: '', ops }).project;
  const inverter = Object.values(p.elements).find(
    (e) => e.symbolId === 'iec.inverter' && !project.elements[e.id],
  );
  const inverterTag = inverter?.tag ?? '';

  const push = (op: Op) => {
    ops.push(op);
    p = applyTransaction(p, { label: '', ops: [op] }).project;
  };
  const board = project.sheets[spec.boardSheetId]!;
  const dcSheetId = newId();
  push({
    op: 'add',
    coll: 'sheets',
    value: {
      id: dcSheetId,
      name: spec.sheetName,
      kind: 'single-line',
      paper: 'A4',
      orientation: 'landscape',
      order: board.order + 0.5,
    },
  });
  const used = new Set(Object.values(p.elements).map((e) => e.tag));
  const tag = (prefix: string) => {
    for (let i = 1; ; i++)
      if (!used.has(`${prefix}${i}`)) {
        used.add(`${prefix}${i}`);
        return `${prefix}${i}`;
      }
  };
  const el = (
    symbolId: string,
    x: number,
    y: number,
    t: string,
    props: Record<string, PropValue> = {},
  ): Id => {
    const value: Element = {
      id: newId(),
      sheetId: dcSheetId,
      symbolId,
      x,
      y,
      rotation: 0,
      mirrored: false,
      tag: t,
      props,
    };
    push({ op: 'add', coll: 'elements', value });
    return value.id;
  };
  const port = (elementId: Id, portId: string): WireEnd => ({ kind: 'port', elementId, portId });
  const pt = (x: number, y: number): WireEnd => ({ kind: 'point', x, y });
  const wire = (from: WireEnd, to: WireEnd, label?: string) =>
    push({
      op: 'add',
      coll: 'wires',
      value: { id: newId(), sheetId: dcSheetId, from, to, via: [], ...(label ? { label } : {}) },
    });

  const STEP = 40;
  const x0 = 40;
  const BUS = 100;
  const xs = spec.strings.map((_, i) => x0 + i * STEP);
  spec.strings.forEach((s, i) => {
    const x = xs[i]!;
    const g = el('iec.pv-string', x - 5, 32, tag('G'), {
      modules: s.modules,
      moduleWp: s.moduleWp,
      ...(s.vocV ? { vocV: s.vocV } : {}),
    });
    const q = el('iec.dc-isolator', x - 5, 62, tag('Q'), { ratingA: 25, voltageV: 1000 });
    wire(port(g, 'out'), port(q, 'in'), 'H1Z2Z2-K 2×1×6');
    wire(port(q, 'out'), pt(x, BUS));
  });
  const last = xs.at(-1) ?? x0;
  let busEnd = last;
  if (spec.dcSpd) {
    const x = last + 25;
    const f = el('iec.spd-dc', x - 5, BUS + 5, tag('F'), { spdType: 'T2' });
    wire(pt(x, BUS), port(f, 'in'));
    const e = el('iec.earth', x - 5, BUS + 30, '');
    wire(port(f, 'pe'), port(e, 'in'));
    busEnd = x;
  }
  if (busEnd > x0) wire(pt(x0, BUS), pt(busEnd, BUS));
  const inv = el('iec.inverter-dc', x0 - 5, BUS + 20, tag('U'), {
    acRef: inverterTag,
    powerKw: spec.inverterKw,
  });
  wire(pt(x0, BUS), port(inv, 'dc'));
  if (spec.batteryKwh && spec.batteryKwh > 0) {
    const b = el('iec.battery', x0 + 5, BUS + 5, tag('G'), { capacityKwh: spec.batteryKwh });
    wire(port(b, 'out'), port(inv, 'bat'));
  }
  el('annot.text', x0 - 5, 22, '', {
    text: `${spec.sheetName} — AC : ${inverterTag || '?'} (${board.name})`,
    size: 3.5,
  });
  return { ops, dcSheetId, inverterTag };
}
